import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, setUnauthorizedHandler, TOKEN_STORAGE_KEY } from '../api/client';

const PLAYER_STORAGE_KEY = 'pastpuzzle.player';

export interface AuthPlayer {
  id: number;
  name: string;
  must_change_password: boolean;
}

interface AuthContextValue {
  player: AuthPlayer | null;
  loading: boolean;
  /** true, wenn ein Token-Hydrate aus localStorage gerade läuft */
  hydrating: boolean;
  login(name: string, password: string): Promise<void>;
  logout(): void;
  /** Wird vom ChangePassword-Flow aufgerufen, sobald der Wechsel
   *  erfolgreich war. Setzt must_change_password lokal auf false. */
  markPasswordChanged(): void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [player, setPlayer] = useState<AuthPlayer | null>(() => {
    const raw = window.localStorage.getItem(PLAYER_STORAGE_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as Partial<AuthPlayer>;
      if (typeof parsed.id !== 'number' || typeof parsed.name !== 'string') return null;
      return {
        id: parsed.id,
        name: parsed.name,
        must_change_password: parsed.must_change_password === true,
      };
    } catch {
      return null;
    }
  });

  const [loading, setLoading] = useState(false);
  const [hydrating, setHydrating] = useState(false);

  const persist = (next: AuthPlayer | null): void => {
    if (next) {
      window.localStorage.setItem(PLAYER_STORAGE_KEY, JSON.stringify(next));
    } else {
      window.localStorage.removeItem(PLAYER_STORAGE_KEY);
    }
    setPlayer(next);
  };

  const logout = useCallback(() => {
    window.localStorage.removeItem(TOKEN_STORAGE_KEY);
    window.localStorage.removeItem(PLAYER_STORAGE_KEY);
    setPlayer(null);
  }, []);

  const login = useCallback(async (name: string, password: string) => {
    setLoading(true);
    try {
      const { data } = await api.post<{ token: string; player: AuthPlayer }>(
        '/auth/login',
        { name, password },
      );
      window.localStorage.setItem(TOKEN_STORAGE_KEY, data.token);
      persist(data.player);
    } finally {
      setLoading(false);
    }
  }, []);

  const markPasswordChanged = useCallback(() => {
    setPlayer((cur) => {
      if (!cur) return cur;
      const next = { ...cur, must_change_password: false };
      window.localStorage.setItem(PLAYER_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => logout());
  }, [logout]);

  // Beim Mount kurz prüfen, ob das Token noch gültig ist (Server-Roundtrip).
  // Wenn nicht, wird der 401-Handler logout() aufrufen.
  useEffect(() => {
    const token = window.localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!token || !player) return;
    setHydrating(true);
    api.get('/players')
      .catch(() => undefined)
      .finally(() => setHydrating(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ player, loading, hydrating, login, logout, markPasswordChanged }),
    [player, loading, hydrating, login, logout, markPasswordChanged],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth muss innerhalb von <AuthProvider> verwendet werden');
  return ctx;
}
